import { NavigatePage } from './components/NavigatePage.js';
import { BusinessLogicPage } from './components/BusinessLogicPage.js';
import 'construct-style-sheets-polyfill';

import { BaseQLClient } from '@adornis/baseql/client/baseqlClient.js';
import { initApp } from '@adornis/functional-lit/init-app.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { makeRouter } from '@adornis/router/client/make-router.js';
import { authenticationGuard } from '@adornis/user-forms/authentication-guard.js';
import { functionalLogin } from '@adornis/user-forms/function-login.js';
import { subscribeCurrentUser } from '@adornis/users/client/currentUser.js';
import { html, nothing } from 'lit';
import '../db/index.js';
import { AnalyticsPage } from './components/AnalyticsPage.js';
import { StudioPage } from './components/StudioPage.js';
import { VaultStudioPage } from './components/VaultStudioPage.js';
import { CallDetail } from './components/CallDetail.js';
import { CallOverview } from './components/CallOverview.js';
import { CallPage } from './components/CallPage.js';
import { Header } from './components/Header.js';
import { LocalTicketDetail } from './components/LocalTicketDetail.js';
import { LocalTicketOverview } from './components/LocalTicketOverview.js';
import { FeedbackWorkspace } from './components/FeedbackWorkspace.js';
import { TestLabPage } from './components/TestLabPage.js';
import { VoicesPage } from './components/VoicesPage.js';
import { TicketDetail } from './components/TicketDetail.js';
import { TicketOverview } from './components/TicketOverview.js';
import './tailwind.css' with { type: 'tailwind' };

BaseQLClient.initDefault();
subscribeCurrentUser();

const router = makeRouter({
  analytics: {
    path: '/analytics',
    render: () => AnalyticsPage(),
  },
  studio: {
    path: '/studio',
    render: () => StudioPage(),
  },
  vault: {
    path: '/vault',
    render: () => VaultStudioPage(),
  },
  root: {
    // 🆕 2026-08-29 — "/" redirects to /call. The header's Tickets link is hidden, so the root was
    // opening a page there is no longer a way to navigate to. A redirect rather than rendering the
    // call page here, so the address bar keeps showing the one canonical /call URL.
    path: '/',
    render: () => {
      // Deferred on purpose: navigating synchronously from inside a render re-enters the router
      // while it is still rendering. A microtask lands before paint, so the redirect is still
      // instant to the eye.
      queueMicrotask(() => goTo('/call'));
      return nothing;
    },
  },
  tickets: {
    // The overview kept a home of its own — hidden from the header is not the same as removed,
    // and a component with no route at all would be an unused import.
    path: '/tickets',
    render: () => TicketOverview(),
  },
  calls: {
    path: '/calls',
    render: () => CallOverview(),
  },
  callDetail: {
    path: '/calls/:callID',
    render: ({ callID }) => CallDetail({ callID }),
  },
  ticket: {
    path: '/ticket/:ticketID',
    render: ({ ticketID }) => TicketDetail({ ticketID: ticketID || '' }),
  },
  navigate: { path: '/navigate', render: () => NavigatePage() },
  businessLogic: { path: '/business-logic', render: () => BusinessLogicPage() },
  call: {
    path: '/call',
    render: () => CallPage(),
  },
  localTickets: {
    path: '/local-tickets',
    render: () => LocalTicketOverview(),
  },
  localTicketDetail: {
    path: '/local-tickets/ticket/:ticketID',
    render: ({ ticketID }) => LocalTicketDetail({ ticketID: ticketID || '' }),
  },
  testLab: {
    path: '/test-lab',
    render: () => TestLabPage(),
  },
  voices: {
    path: '/voices',
    render: () => VoicesPage(),
  },
  testLabFeedback: {
    path: '/test-lab/feedback',
    render: () => FeedbackWorkspace(),
  },
}).init();

const App = stateful(() => {
  return authenticationGuard(
    stateful(
      () => html`
        <div class="min-h-screen h-screen bg-base-200 flex flex-col">
          ${Header()}
          <main class="flex-1 overflow-y-auto">${router.outlet.value}</main>
        </div>
      `,
    ),
    () => functionalLogin({}),
  );
});

initApp(App);
